/**
 * Clipboard scene parser
 */

import type {
  ClipboardParseResult,
  FigmaInternalScene,
  NodeType,
  SceneNode,
  VariableRegistry,
} from './types/scene.js'

/**
 * Validation result
 */
export interface ValidationResult {
  valid: boolean
  errors: string[]
}

/**
 * Parser for Figma's internal clipboard scene graph
 */
export class ClipboardSceneParser {
  /**
   * Check if a node is visible
   *
   * @param node - The node to check
   * @returns True if the node is visible (visible is undefined or true)
   */
  isVisible(node: SceneNode): boolean {
    const props = node.properties as any
    // If visible is undefined or true, the node is visible
    // Only false means hidden
    return props?.visible !== false
  }

  /**
   * Parse the first child from the scene's children list
   *
   * Navigates: root (DOCUMENT) -> children[0] (CANVAS) -> children[0] (first item)
   *
   * @param sceneData - The parsed Figma internal scene
   * @param filterHidden - If true, skip hidden nodes (default: false)
   * @returns The first child node, or null if not found
   */
  parseFirstChild(
    sceneData: FigmaInternalScene,
    filterHidden = false,
  ): SceneNode | null {
    const canvas = sceneData.root.children?.[0]
    if (!canvas || canvas.type !== 'CANVAS') {
      return null
    }

    if (!canvas.children || canvas.children.length === 0) {
      return null
    }

    if (filterHidden) {
      // Find first visible child
      for (const child of canvas.children) {
        if (this.isVisible(child)) {
          return child
        }
      }
      return null
    }

    const firstChild = canvas.children[0]
    return firstChild || null
  }

  /**
   * Validate a scene node
   *
   * @param node - The node to validate
   * @returns Validation result with errors if any
   */
  validateNode(node: SceneNode): ValidationResult {
    const errors: string[] = []

    // Check required fields
    if (!node.guid) {
      errors.push('Node missing required field: guid')
    }
    if (!node.type) {
      errors.push('Node missing required field: type')
    }
    if (!node.name) {
      errors.push('Node missing required field: name')
    }

    // Validate GUID format (should be "sessionID:localID")
    if (node.guid && !node.guid.includes(':')) {
      errors.push(`Invalid GUID format: ${node.guid}`)
    }

    // Validate children array
    if (node.children && !Array.isArray(node.children)) {
      errors.push('Node children must be an array')
    }

    // Validate properties match type
    if (node.properties && node.properties.type !== node.type) {
      errors.push(`Node type mismatch: ${node.type} vs ${node.properties.type}`)
    }

    return {
      valid: errors.length === 0,
      errors,
    }
  }

  /**
   * Extract all nodes of a specific type from the scene graph
   *
   * @param root - The root node to start searching from
   * @param type - The node type to find
   * @returns Array of matching nodes
   */
  findNodesByType(root: SceneNode, type: NodeType): SceneNode[] {
    const results: SceneNode[] = []

    const traverse = (node: SceneNode) => {
      if (node.type === type) {
        results.push(node)
      }
      for (const child of node.children || []) {
        traverse(child)
      }
    }

    traverse(root)
    return results
  }

  /**
   * Find a node by its GUID
   *
   * @param root - The root node to start searching from
   * @param guid - The GUID to search for
   * @returns The matching node, or null if not found
   */
  findNodeByGuid(root: SceneNode, guid: string): SceneNode | null {
    if (root.guid === guid) {
      return root
    }

    for (const child of root.children || []) {
      const found = this.findNodeByGuid(child, guid)
      if (found) {
        return found
      }
    }

    return null
  }

  /**
   * Count total nodes in the scene graph
   *
   * @param root - The root node to start counting from
   * @returns Total number of nodes
   */
  countNodes(root: SceneNode): number {
    let count = 1 // Count the root
    for (const child of root.children || []) {
      count += this.countNodes(child)
    }
    return count
  }

  /**
   * Parse variable registry from "Internal Only Canvas"
   *
   * Navigates: root (DOCUMENT) -> children[1] (CANVAS "Internal Only Canvas")
   *
   * @param sceneData - The parsed Figma internal scene
   * @returns Variable registry or null if not found
   */
  parseVariableRegistry(sceneData: FigmaInternalScene): VariableRegistry | null {
    const internalCanvas = sceneData.root.children?.[1]

    if (!internalCanvas || internalCanvas.type !== 'CANVAS') {
      return null
    }

    if (internalCanvas.name !== 'Internal Only Canvas') {
      return null
    }

    const variableSets = this.findNodesByType(internalCanvas, 'VARIABLE_SET')
    const variables = this.findNodesByType(internalCanvas, 'VARIABLE')

    return {
      canvas: internalCanvas,
      variableSets,
      variables,
    }
  }

  /**
   * Parse both component and variable registry
   *
   * @param sceneData - The parsed Figma internal scene
   * @param filterHidden - If true, skip hidden nodes in component (default: false)
   * @returns Parse result with component and variable registry
   */
  parseClipboard(
    sceneData: FigmaInternalScene,
    filterHidden = false,
  ): ClipboardParseResult {
    return {
      component: this.parseFirstChild(sceneData, filterHidden),
      variableRegistry: this.parseVariableRegistry(sceneData),
    }
  }
}
